import React, { useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward } from 'lucide-react';

export default function MusicPlayer({ tracks = [], artistName = 'Artist' }) {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!audioRef.current || tracks.length === 0) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const handleVolumeChange = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newVolume = Math.max(0, Math.min(1, percent));
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume > 0) setIsMuted(false);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const nextTrack = () => {
    if (tracks.length === 0) return;
    setCurrentTrack((prev) => (prev + 1) % tracks.length);
    setIsPlaying(false);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const prevTrack = () => {
    if (tracks.length === 0) return;
    setCurrentTrack((prev) => (prev - 1 + tracks.length) % tracks.length);
    setIsPlaying(false);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }, 100);
  };

  const handleEnded = () => {
    if (currentTrack < tracks.length - 1) {
      nextTrack();
    } else {
      setIsPlaying(false);
      setProgress(0);
    }
  };

  if (tracks.length === 0) {
    return (
      <div className="bg-white/5 rounded-xl p-4 text-center" data-testid="music-player-empty">
        <p className="text-white/40 text-sm">No audio tracks available</p>
      </div>
    );
  }

  const track = tracks[currentTrack];

  return (
    <div className="bg-gradient-to-br from-violet-900/30 to-purple-900/20 rounded-xl p-4 border border-white/10" data-testid="music-player">
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={track?.url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* Track Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
          <div className={`w-6 h-6 rounded-full bg-violet-500 ${isPlaying ? 'animate-pulse' : ''}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{track?.title || 'Track'}</p>
          <p className="text-white/40 text-sm truncate">{artistName}</p>
        </div>
        <span className="text-white/40 text-xs">
          {currentTrack + 1} / {tracks.length}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div 
          className="h-1.5 bg-white/10 rounded-full cursor-pointer group"
          onClick={handleSeek}
        >
          <div 
            className="h-full bg-violet-500 rounded-full relative group-hover:bg-violet-400 transition-colors"
            style={{ width: `${(progress / duration) * 100 || 0}%` }}
          >
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-white/40 text-xs">{formatTime(progress)}</span>
          <span className="text-white/40 text-xs">{formatTime(duration)}</span>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        {/* Volume */}
        <div className="flex items-center gap-2 w-24">
          <button 
            onClick={toggleMute}
            className="text-white/60 hover:text-white transition-colors"
          >
            {isMuted || volume === 0 ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
          <div 
            className="flex-1 h-1 bg-white/10 rounded-full cursor-pointer"
            onClick={handleVolumeChange}
          >
            <div 
              className="h-full bg-white/40 rounded-full"
              style={{ width: `${(isMuted ? 0 : volume) * 100}%` }}
            />
          </div>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center gap-4">
          <button 
            onClick={prevTrack}
            className="text-white/60 hover:text-white transition-colors"
            disabled={tracks.length <= 1}
          >
            <SkipBack className="w-5 h-5" />
          </button>
          
          <button 
            onClick={togglePlay}
            className="w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center transition-colors"
            data-testid="play-pause-btn"
          >
            {isPlaying ? (
              <Pause className="w-5 h-5 text-white" />
            ) : (
              <Play className="w-5 h-5 text-white ml-0.5" />
            )}
          </button>
          
          <button 
            onClick={nextTrack}
            className="text-white/60 hover:text-white transition-colors"
            disabled={tracks.length <= 1}
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>

        {/* Spacer */}
        <div className="w-24" />
      </div>
    </div>
  );
}
